import {
  QueryKey,
  QueryObserverOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import { TableQueryState } from 'components/Table/useTableQueryState';
import { queryClient } from 'query-client';
import { toast } from 'react-toastify';
import { CoreBaseConfiguration } from './BaseConfiguration';
import {
  CaseImportersApi,
  OrganizationCaseImporterCreateRequest,
  OrganizationCaseImporterResponsePagedData,
  OrganizationCaseImporterUpdateRequest,
} from './core';
import { CaseApiKeys } from './useCasesApi';

const CaseImporterApiKeys = {
  GET_CASE_IMPORTER_DEFINITIONS: 'case-importer-definitions',
  GET_CASE_IMPORTERS_SEARCH: 'case-importers-search',
  GET_CASE_IMPORTERS: 'case-importers',
  GET_CASE_IMPORTER: 'case-importer',
};

const client = new CaseImportersApi(new CoreBaseConfiguration());

export const useSearchCaseImporters = ({
  searchTerm,
}: {
  searchTerm?: string;
}) => {
  return useQuery({
    queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTERS_SEARCH, searchTerm],
    queryFn: () =>
      client.caseImportersSearchGet({
        size: 50,
        searchTerm,
      }),
    enabled: searchTerm !== undefined || searchTerm != '',
  });
};

export const useGetCaseImporterDefinitions = (state?: TableQueryState) => {
  return useQuery({
    queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTER_DEFINITIONS, state],
    queryFn: () =>
      client.caseImportersDefinitionsGet({
        pageNumber: state?.pagination.pageIndex,
        size: state?.pagination.pageSize,
        orderByProperty: state?.sorting[0]?.id,
        ascending: state?.sorting[0]?.desc === false,
        searchTerm: state?.globalFilter,
      }),
  });
};

export const useGetCaseImporters = (
  options: Omit<
    QueryObserverOptions<
      OrganizationCaseImporterResponsePagedData,
      Error,
      OrganizationCaseImporterResponsePagedData,
      OrganizationCaseImporterResponsePagedData,
      QueryKey
    >,
    'queryKey'
  > = {},
  state?: TableQueryState,
  projectId?: string
) => {
  return useQuery({
    queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTERS, state, options],
    queryFn: () =>
      client.caseImportersGet({
        pageNumber: state?.pagination.pageIndex,
        size: state?.pagination.pageSize,
        orderByProperty: state?.sorting[0]?.id,
        ascending: state?.sorting[0]?.desc === false,
        searchTerm: state?.globalFilter,
        projectId: projectId ?? undefined,
      }),
    ...options,
  });
};

export const useGetCaseImporter = (id?: string | null) => {
  return useQuery({
    queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTER, id],
    queryFn: () => client.caseImportersIdGet({ id: id ?? '' }),
    enabled: id !== undefined && id !== null,
  });
};

export const useCreateCaseImporter = () => {
  return useMutation({
    mutationFn: ({
      organizationCaseImporterCreateRequest,
    }: {
      organizationCaseImporterCreateRequest: OrganizationCaseImporterCreateRequest;
    }) => client.caseImportersPost({ organizationCaseImporterCreateRequest }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTERS],
      });
      toast.success(
        `Integrationen '${e.caseImporter?.name}' er blevet oprettet`,
        {
          toastId: 'createCaseImporter',
        }
      );
    },
  });
};

export const useUpdateCaseImporter = () => {
  return useMutation({
    mutationFn: ({
      id,
      organizationCaseImporterUpdateRequest,
    }: {
      id: string;
      organizationCaseImporterUpdateRequest: OrganizationCaseImporterUpdateRequest;
    }) =>
      client.caseImportersIdPut({ id, organizationCaseImporterUpdateRequest }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTERS],
      });
      queryClient.invalidateQueries({
        queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTER, e.id],
      });
      toast.success(
        `Integrationen '${e.caseImporter?.name}' er blevet opdateret`,
        {
          toastId: 'updateCaseImporter',
        }
      );
    },
  });
};

export const useDeleteCaseImporter = () => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.caseImportersIdDelete({ id }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTERS],
      });
      queryClient.invalidateQueries({
        queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTER, e.id],
      });
      toast.success(`Integrationen er blevet slettet`, {
        toastId: 'deleteCaseImporter',
      });
    },
  });
};

export const useResetCaseHashesForCaseImporter = () => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.caseImportersIdResetCaseHashesPut({ id }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [CaseApiKeys.GET_CASES, e.projectId],
      });
      toast.success(
        `Sagerne for integrationen '${e.caseImporter?.name}' er blevet markeret som ikke synkroniseret`,
        {
          toastId: 'resetCaseHashesForCaseImporter',
        }
      );
    },
  });
};

export const useInvokeCaseImporter = () => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.caseImportersIdInvokePut({ id }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTERS],
      });
      queryClient.invalidateQueries({
        queryKey: [CaseImporterApiKeys.GET_CASE_IMPORTER, e.id],
      });
      toast.success(`Integrationen '${e.caseImporter?.name}' er køres`, {
        toastId: 'invokeCaseImporter',
      });
    },
  });
};
